import React, { useEffect, useState } from 'react';
import { CCard, CCardBody, CCardHeader, CCardTitle, CFormGroup, CLabel } from '@coreui/react';
import { useMutation, useQueryClient } from 'react-query';
import _t from 'counterpart';
import ButtonWithLoader from '../../components/ButtonWithLoader';
import { extractErrorMessage, handleFormError } from '../../helpers';
import toast from 'react-hot-toast';
import { useGetSetting, useSetSetting } from './hooks';
import { CSwitch } from '@coreui/react';

const CustomerDataMaskingSettings = () => {
  const [enabled, setEnabled] = useState<boolean>(false);
  const [originalValue, setOriginalValue] = useState<boolean>(false);

  const queryClient = useQueryClient();
  const setSettingMutation = useSetSetting();

  const dataMaskingEnabledQuery = useGetSetting('CUSTOMER_DATA_MASKING_ENABLED', 'customer-data-masking-enabled', {
    retry: false,
    refetchOnWindowFocus: false,
    onError: (e: any) => {
      toast.error(extractErrorMessage(e));
    },
  });

  const updateMutation = useMutation(
    () => setSettingMutation.mutateAsync({ key: 'CUSTOMER_DATA_MASKING_ENABLED', value: enabled }),
    {
      onSuccess: async () => {
        toast.success(_t('customer-data-masking.successfully-updated'));
        await queryClient.invalidateQueries(['customer-data-masking-enabled']);
        setOriginalValue(enabled);
        await dataMaskingEnabledQuery.refetch();
      },
      onError: async (e: any) => {
        if (e.response?.status !== 422) {
          handleFormError(e);
        }
      },
    }
  );

  useEffect(() => {
    if (dataMaskingEnabledQuery.data !== undefined) {
      const isEnabled = Boolean(dataMaskingEnabledQuery.data);
      setEnabled(isEnabled);
      setOriginalValue(isEnabled);
    }
  }, [dataMaskingEnabledQuery.data]);

  const hasChanges = enabled !== originalValue;

  const invokeMutation = () => {
    updateMutation.mutate();
  };

  if (dataMaskingEnabledQuery.isLoading) {
    return (
      <CCard className="mt-4">
        <CCardHeader>
          <CCardTitle>{_t('customer-data-masking.title')}</CCardTitle>
          <p className="mb-0 mt-1 small">{_t('customer-data-masking.description')}</p>
        </CCardHeader>
        <CCardBody>
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        </CCardBody>
      </CCard>
    );
  }

  return (
    <CCard className="mt-4">
      <CCardHeader>
        <CCardTitle>{_t('customer-data-masking.title')}</CCardTitle>
        <p className="mb-0 mt-1 small">{_t('customer-data-masking.description')}</p>
      </CCardHeader>
      <CCardBody>
        <CFormGroup>
          <div className="mt-2">
            <CSwitch
              color="primary"
              checked={enabled}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEnabled(e.target.checked)}
              disabled={dataMaskingEnabledQuery.isLoading || updateMutation.isLoading}
            />
            <span className="ml-2">
              {enabled ? _t('customer-data-masking.enabled') : _t('customer-data-masking.disabled')}
            </span>
          </div>
          <small className="text-muted mt-2 d-block">{_t('customer-data-masking.help-text')}</small>
        </CFormGroup>

        <ButtonWithLoader
          isLoading={updateMutation.isLoading}
          onClick={invokeMutation}
          buttonColor="primary"
          spinnerColor="secondary"
          title={_t('action.save')}
          className="btn-md"
          showText={false}
          disabled={!hasChanges}
        />
      </CCardBody>
    </CCard>
  );
};

export default CustomerDataMaskingSettings;
